// @ts-nocheck
import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { resolveProcessGroup } from "./processGrouping";
import { useQuery } from "@tanstack/react-query";
import { Pencil, AlertCircle, Trash2, Gauge, Clock, Layers, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, formatNumber, renderDetailValue } from "@/lib/utils";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { DetailField as F } from "@/components/shared/DetailField";


const PROCESS_GROUP_LABEL: Record<string, string> = {
  TRUOC_IN: "Trước in",
  IN: "In",
  SAU_IN: "Sau in",
  THANH_PHAM: "Thành phẩm",
  KHAC: "Khác",
};

export default function ProcessDetailPage() {
  const navigate = useNavigate();
  const { processId } = useParams({ strict: false }) as { processId: string };

  const {
    data: pr,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useQuery({
    queryKey: ["process", processId],
    queryFn: async () =>
      trpcClient.technical.processes.getById.query({ id: processId }),
  });

  const d = pr as any;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = async () => {
    try {
      await trpcClient.technical.processes.delete.mutate({
        id: d.stepId || processId,
      });

      navigate({ to: "/processes" });
    } catch (error: any) {
      toast.error(error?.message || "Xoá thất bại");
    }
  };

  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Công đoạn", to: "/processes" }]} />;
  }

  if (error || !d) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Công đoạn", to: "/processes" }]}
        entityLabel="Công đoạn"
        backTo="/processes"
        codeOrId={processId}
        error={error}
        onRetry={() => refetchDetail()}
      />
    );
  }

  // capabilities badges
  const caps: string[] = [];
  if (d.cmyk) caps.push("CMYK");
  if (d.pantone) caps.push("Pantone");
  if (d.requiresDie) caps.push("Cần khuôn");

  const specs = (d.specs && typeof d.specs === "object" ? d.specs : {}) as Record<string, any>;
  const norms = Array.isArray(specs.norms) ? specs.norms : [];

  return (
    <StandardDetailLayout
      breadcrumbs={[{ label: "Công đoạn", to: "/processes" }]}
      title={d.stepId || processId}
      entityId={d?.id || processId}
      entityType="process"
      timestamps={{ created: d.createdAt, updated: d.updatedAt }}
      actions={
        <div className="flex gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() =>
              (navigate as any)({
                to: "/processes/$processId/edit",
                params: { processId },
              })
            }
          >
            <Pencil size={14} className="mr-1.5" /> Sửa
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 size={14} className="mr-1.5" /> Xoá
          </Button>
        </div>
      }
      onBack={() => navigate({ to: "/processes" })}
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Xác nhận xoá quy trình"
        description={
          <>
            Bạn có chắc chắn muốn xoá quy trình <strong>{d.name}</strong> không?
            Hành động này không thể hoàn tác.
          </>
        }
        confirmText="Xoá"
        onConfirm={handleDelete}
        variant="destructive"
      />
      <div className="space-y-6">
        {/* 1. THÔNG TIN CHUNG & ĐỊNH MỨC CỐT LÕI */}
        <div className="border rounded-lg bg-white shadow-sm p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
            <F label="Mã công đoạn" value={d.stepId} mono hl />
            <F label="Tên" value={d.name} />
            <F
              label="Nhóm"
              value={
                d.group ? (
                  <Link
                    to="/processes"
                    search={{ group: resolveProcessGroup(d) } as any}
                    className="text-indigo-600 hover:underline"
                    title="Xem các công đoạn cùng nhóm"
                  >
                    {PROCESS_GROUP_LABEL[resolveProcessGroup(d)] || d.group}
                  </Link>
                ) : undefined
              }
            />
          </div>
          <div className="border-t border-slate-100" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
            <F label="ĐVT" value={d.unit} />
            <F label="Loại máy mặc định" value={d.defaultMachineType} />
            <F label="Thứ tự" value={d.sortOrder} />
          </div>

          <div className="border-t border-slate-100" />

          {/* ĐỊNH MỨC CÔNG SUẤT VÀ LẬP LỊCH APS (PHASE 61) */}
          <div className="p-3 bg-indigo-50/40 rounded-lg border border-indigo-100">
            <div className="flex items-center gap-1.5 mb-2 text-indigo-900 font-semibold text-xs uppercase tracking-wide">
              <Gauge className="w-3.5 h-3.5 text-indigo-600" />
              <span>Định mức lập lịch & công suất chuẩn xưởng (APS / MES)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
              <F
                label="TG Setup Chuẩn"
                value={d.defaultSetupTime !== null && d.defaultSetupTime !== undefined ? `${d.defaultSetupTime} phút` : undefined}
                hl
              />
              <F
                label="Tốc Độ Chuẩn"
                value={
                  d.defaultSpeed || d.speed
                    ? `${formatNumber(d.defaultSpeed || d.speed)} ${d.unit || "tờ"}/h`
                    : undefined
                }
                hl
              />
              <F
                label="Hao Phí Setup"
                value={
                  d.defaultSetupWaste !== null && d.defaultSetupWaste !== undefined
                    ? `${formatNumber(d.defaultSetupWaste)} ${d.unit || "tờ"}`
                    : d.minWasteQty
                    ? `${formatNumber(d.minWasteQty)} ${d.unit || "tờ"}`
                    : undefined
                }
              />
              <F
                label="% Bù Hao Chạy"
                value={
                  d.defaultRunWastePct !== null && d.defaultRunWastePct !== undefined
                    ? `${d.defaultRunWastePct}%`
                    : d.wastePercent
                    ? `${d.wastePercent}%`
                    : undefined
                }
              />
            </div>
          </div>

          {/* THÔNG SỐ VẬN HÀNH CA MÁY PHỤ TRỢ (EXCEL) */}
          {(specs.changeoverTime || specs.washTime || specs.warmupTime || specs.colorMatchingTime || specs.oldDieWithBaseSetupTime) && (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-1.5 mb-2 text-slate-800 font-semibold text-xs uppercase tracking-wide">
                <Clock className="w-3.5 h-3.5 text-slate-600" />
                <span>Thông số vận hành phụ trợ ca máy (Excel ký duyệt)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
                {specs.changeoverTime !== undefined && (
                  <F label="Chuyển bài cùng dạng" value={`${specs.changeoverTime} phút`} />
                )}
                {specs.washTime !== undefined && (
                  <F label="Rửa máy / Đổi mực" value={`${specs.washTime} phút`} />
                )}
                {specs.warmupTime !== undefined && (
                  <F label="Chờ nhiệt đầu ngày" value={`${specs.warmupTime} phút`} />
                )}
                {specs.colorMatchingTime !== undefined && (
                  <F label="Lấy màu bài in" value={`${specs.colorMatchingTime} phút`} />
                )}
                {specs.oldDieWithBaseSetupTime !== undefined && (
                  <F label="Khuôn cũ đế sẵn" value={`${specs.oldDieWithBaseSetupTime} phút`} />
                )}
              </div>
            </div>
          )}

          <div className="border-t border-slate-100" />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2">
            <F
              label="Chi phí setup"
              value={d.setupCost ? formatMoney(d.setupCost) : undefined}
              mono
            />
            <F
              label="Đơn giá"
              value={d.unitCost ? formatMoney(d.unitCost) : undefined}
              mono
            />
            <F
              label="TG SX TB"
              value={
                d.productionTime ? `${d.productionTime} phút/1000` : undefined
              }
            />
          </div>

          {caps.length > 0 && (
            <>
              <div className="border-t border-slate-100" />
              <div className="flex gap-2 min-w-0">
                <span className="text-slate-500 text-xs shrink-0 w-28">
                  Năng lực
                </span>
                <div className="flex flex-wrap gap-1">
                  {caps.map((c) => (
                    <Badge
                      key={c}
                      variant="outline"
                      className="text-[10px] h-5 px-2"
                    >
                      {c}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}
          {d.description && (
            <>
              <div className="border-t border-slate-100" />
              <div className="flex gap-2 min-w-0">
                <span className="text-slate-500 text-xs shrink-0 w-28">
                  Mô tả
                </span>
                <span className="text-sm text-slate-700 whitespace-pre-wrap">
                  {d.description}
                </span>
              </div>
            </>
          )}
        </div>

        {/* 2. BẢNG MA TRẬN ĐỊNH MỨC BIẾN THỂ TỪ EXCEL */}
        {norms.length > 0 && (
          <Card className="border-slate-200 shadow-sm overflow-hidden bg-white">
            <CardHeader className="py-3 px-4 bg-slate-50/70 border-b border-slate-200">
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-indigo-600" />
                  <span>Ma Trận Định Mức Biến Thể Chi Tiết ({norms.length} cấu hình)</span>
                </div>
                <Badge variant="outline" className="text-[11px] bg-white text-indigo-700 border-indigo-200">
                  Nguồn: KHSX - Sản Xuất - Kỹ Thuật
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                      <TableHead className="w-[50px] text-center">STT</TableHead>
                      <TableHead>Dạng bài / Nhóm hàng</TableHead>
                      {norms.some((n: any) => n.ups !== undefined) && (
                        <TableHead className="w-[80px] text-center">Số Bát (Ups)</TableHead>
                      )}
                      <TableHead className="text-center">Setup bài đầu / Khuôn mới</TableHead>
                      {norms.some((n: any) => n.colorMatchingTime !== undefined || n.strippingPins !== undefined) && (
                        <TableHead className="text-center">Lấy màu / Chọc phôi</TableHead>
                      )}
                      <TableHead className="text-center">Tốc độ chuẩn</TableHead>
                      {norms.some((n: any) => n.washTime !== undefined) && (
                        <TableHead className="text-center">Rửa máy</TableHead>
                      )}
                      {norms.some((n: any) => n.changeoverTime !== undefined) && (
                        <TableHead className="text-center">Chuyển bài</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {norms.map((norm: any, index: number) => {
                      const catName = norm.category || norm.jobCategory || `Cấu hình ${index + 1}`;
                      const setupVal = norm.setupTime ?? norm.setupNewDie;
                      const adjVal = norm.colorMatchingTime ?? norm.strippingPins;

                      return (
                        <TableRow key={index} className="hover:bg-slate-50/80 text-xs">
                          <TableCell className="text-center text-slate-400 font-mono">
                            {index + 1}
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">
                            {catName}
                          </TableCell>
                          {norms.some((n: any) => n.ups !== undefined) && (
                            <TableCell className="text-center">
                              {norm.ups ? (
                                <Badge variant="outline" className="font-semibold text-[11px] bg-slate-50">
                                  {norm.ups} bát
                                </Badge>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                          )}
                          <TableCell className="text-center font-semibold text-slate-700">
                            {setupVal !== undefined ? `${setupVal} phút` : "—"}
                          </TableCell>
                          {norms.some((n: any) => n.colorMatchingTime !== undefined || n.strippingPins !== undefined) && (
                            <TableCell className="text-center text-slate-600">
                              {adjVal !== undefined ? `${adjVal} phút` : "—"}
                            </TableCell>
                          )}
                          <TableCell className="text-center font-bold text-indigo-700">
                            {norm.speed ? `${formatNumber(norm.speed)} ${d.unit || "tờ"}/h` : "—"}
                          </TableCell>
                          {norms.some((n: any) => n.washTime !== undefined) && (
                            <TableCell className="text-center text-slate-600">
                              {norm.washTime !== undefined ? `${norm.washTime} phút` : "—"}
                            </TableCell>
                          )}
                          {norms.some((n: any) => n.changeoverTime !== undefined) && (
                            <TableCell className="text-center text-slate-600">
                              {norm.changeoverTime !== undefined ? `${norm.changeoverTime} phút` : "—"}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </StandardDetailLayout>
  );
}
